import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiSettings, FiUser, FiLock, FiBell, FiShield, FiSave, FiInfo, FiGlobe, FiMail, FiServer, FiCpu, FiClock, FiDatabase, FiUsers, FiMessageCircle, FiActivity } from 'react-icons/fi';

const SectionHeader = ({ icon, title, subtitle }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eef3e6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7ea52e', fontSize: 16 }}>{icon}</div>
    <div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0, fontFamily: 'Poppins, sans-serif' }}>{title}</h3>
      {subtitle && <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0' }}>{subtitle}</p>}
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--border-color)' }}>
    <span style={{ fontSize: 14, fontWeight: 500, color: '#334155' }}>{label}</span>
    <button
      onClick={() => onChange(!checked)}
      style={{
        width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
        background: checked ? '#7ea52e' : '#cbd5e1', position: 'relative', transition: 'background 0.2s',
      }}
    >
      <span style={{
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3, left: checked ? 25 : 3,
        transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }} />
    </button>
  </div>
);

export default function AdminSettings() {
  const { user } = useAuth();

  const [profile, setProfile] = useState({
    username: user?.username || '',
    email: user?.email || '',
  });
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [notifications, setNotifications] = useState({ loginAlerts: true, campaignComplete: true, failureAlerts: true, dailyDigest: false });
  const [loadingNotifs, setLoadingNotifs] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [systemInfo, setSystemInfo] = useState(null);
  const [loadingSystem, setLoadingSystem] = useState(true);

  // Fetch notification preferences from database
  const fetchNotificationPrefs = useCallback(async () => {
    setLoadingNotifs(true);
    try {
      const res = await api.get('/auth/notification-prefs');
      setNotifications({
        loginAlerts: res.data.loginAlerts ?? true,
        campaignComplete: res.data.campaignComplete ?? true,
        failureAlerts: res.data.failureAlerts ?? true,
        dailyDigest: res.data.dailyDigest ?? false,
      });
    } catch {
      // fallback defaults
    }
    finally { setLoadingNotifs(false); }
  }, []);

  // Persist notification preferences to database
  const updateNotification = async (key, value) => {
    const updated = { ...notifications, [key]: value };
    setNotifications(updated);
    try {
      await api.put('/auth/notification-prefs', { [key]: value });
      toast.success('Preference saved');
    } catch {
      // revert on failure
      setNotifications(notifications);
      toast.error('Failed to save preference');
    }
  };

  // Fetch dynamic system info
  const fetchSystemInfo = useCallback(async () => {
    setLoadingSystem(true);
    try {
      const res = await api.get('/admin/system-info');
      setSystemInfo(res.data);
    } catch { setSystemInfo(null); }
    finally { setLoadingSystem(false); }
  }, []);

  useEffect(() => { fetchSystemInfo(); fetchNotificationPrefs(); }, [fetchSystemInfo, fetchNotificationPrefs]);

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      await api.put('/auth/profile', { email: profile.email });
      toast.success('Profile updated!');
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setSavingProfile(false); }
  };

  const handlePasswordChange = async () => {
    if (passwords.newPass !== passwords.confirm) return toast.error('Passwords do not match');
    if (passwords.newPass.length < 6) return toast.error('Password must be at least 6 characters');
    setSavingPassword(true);
    try {
      await api.put('/auth/profile', { currentPassword: passwords.current, newPassword: passwords.newPass });
      toast.success('Password changed successfully!');
      setPasswords({ current: '', newPass: '', confirm: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Password change failed'); }
    finally { setSavingPassword(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage your account, security & preferences</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* ── Profile Section ── */}
        <div className="card">
          <SectionHeader icon={<FiUser />} title="Profile" subtitle="Your account details" />

          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, padding: 16, borderRadius: 14, background: 'var(--bg-primary)' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'linear-gradient(135deg, #7ea52e, #1a1a18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, fontWeight: 800, color: '#fff',
            }}>{user?.username?.[0]?.toUpperCase() || 'A'}</div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{user?.username}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{user?.email}</div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#eef3e6', color: '#7ea52e', textTransform: 'uppercase', letterSpacing: 0.5 }}>{user?.role}</span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Username</label>
            <input type="text" className="form-input" value={profile.username}
              onChange={e => setProfile({ ...profile, username: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" value={profile.email}
              onChange={e => setProfile({ ...profile, email: e.target.value })} />
          </div>
          <button className="btn btn-primary" onClick={handleProfileSave} disabled={savingProfile}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <FiSave /> {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </div>

        {/* ── Security Section ── */}
        <div className="card">
          <SectionHeader icon={<FiLock />} title="Security" subtitle="Change your password" />

          <div style={{ padding: 14, borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <FiInfo style={{ color: '#3b82f6', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>
              Password must be at least 6 characters. Use a mix of letters, numbers, and symbols for better security.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input type="password" className="form-input" value={passwords.current} placeholder="Enter current password"
              onChange={e => setPasswords({ ...passwords, current: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">New Password</label>
            <input type="password" className="form-input" value={passwords.newPass} placeholder="Enter new password"
              onChange={e => setPasswords({ ...passwords, newPass: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input type="password" className="form-input" value={passwords.confirm} placeholder="Confirm new password"
              onChange={e => setPasswords({ ...passwords, confirm: e.target.value })} />
          </div>
          <button className="btn btn-primary" onClick={handlePasswordChange} disabled={savingPassword}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <FiShield /> {savingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* ── Notification Preferences ── */}
        <div className="card">
          <SectionHeader icon={<FiBell />} title="Notifications" subtitle="Control what alerts you receive" />
          <Toggle label="Login alerts" checked={notifications.loginAlerts} onChange={v => updateNotification('loginAlerts', v)} />
          <Toggle label="Campaign completion alerts" checked={notifications.campaignComplete} onChange={v => updateNotification('campaignComplete', v)} />
          <Toggle label="High failure rate warnings" checked={notifications.failureAlerts} onChange={v => updateNotification('failureAlerts', v)} />
          <Toggle label="Daily digest email" checked={notifications.dailyDigest} onChange={v => updateNotification('dailyDigest', v)} />
          {loadingNotifs && <p style={{ fontSize: 11, color: '#94a3b8', margin: '12px 0 0', fontStyle: 'italic' }}>Loading preferences...</p>}
        </div>

        {/* ── System Info ── */}
        <div className="card">
          <SectionHeader icon={<FiServer />} title="System Info" subtitle="Live portal configuration" />
          {loadingSystem ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Loading system info...</div>
          ) : systemInfo ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                { icon: <FiGlobe />, label: 'Portal', value: systemInfo.portal?.name || systemInfo.portal?.portalId || 'N/A' },
                { icon: <FiShield />, label: 'Portal Status', value: systemInfo.portal?.status?.toUpperCase() || 'N/A' },
                { icon: <FiUsers />, label: 'Team Members', value: `${systemInfo.stats?.activeUsers || 0} active / ${systemInfo.stats?.totalUsers || 0} total` },
                { icon: <FiMessageCircle />, label: 'Campaigns', value: `${systemInfo.stats?.activeCampaigns || 0} active / ${systemInfo.stats?.totalCampaigns || 0} total` },
                { icon: <FiMail />, label: 'Channels in Use', value: `${systemInfo.stats?.campaignTypesInUse || 0} types` },
                { icon: <FiDatabase />, label: 'Master Data', value: `${(systemInfo.stats?.totalMasterData || 0).toLocaleString()} records` },
                { icon: <FiActivity />, label: 'Messages Delivered', value: `${(systemInfo.stats?.totalSent || 0).toLocaleString()} sent / ${(systemInfo.stats?.totalFailed || 0).toLocaleString()} failed` },
                { icon: <FiCpu />, label: 'Server Node', value: systemInfo.server?.nodeVersion || 'N/A' },
                { icon: <FiClock />, label: 'Server Uptime', value: systemInfo.server?.uptime || 'N/A' },
                { icon: <FiUser />, label: 'Last Activity', value: systemInfo.server?.lastActivity ? new Date(systemInfo.server.lastActivity).toLocaleString() : 'N/A' },
              ].map((item, i, arr) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border-color)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: '#94a3b8', fontSize: 15 }}>{item.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>{item.label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{item.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Could not load system info</div>
          )}
        </div>
      </div>
    </div>
  );
}
